import React, { useEffect, useState } from 'react';
import { Spinner, useStateContext } from '@veda/dashboard-ui';
import { usePppApi } from '../services/pppApi';
import { useDownload } from '../hooks/useDownload';

function StatoBadge({ stato }) {
  const map = {
    rispettata: 'bg-green-100 text-green-700',
    scaduta:    'bg-red-100 text-red-700',
    prorogata:  'bg-yellow-100 text-yellow-700',
    attiva:     'bg-blue-100 text-blue-700',
    sospesa:    'bg-gray-100 text-gray-600',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${map[stato] ?? 'bg-gray-100 text-gray-600'}`}>
      {stato}
    </span>
  );
}

function KpiCard({ label, value, color }) {
  const colors = {
    blue:   'bg-blue-50 dark:bg-blue-900/20 text-blue-700',
    green:  'bg-green-50 dark:bg-green-900/20 text-green-700',
    red:    'bg-red-50 dark:bg-red-900/20 text-red-700',
    orange: 'bg-orange-50 dark:bg-orange-900/20 text-orange-700',
  };
  return (
    <div className={`rounded-xl p-4 text-center ${colors[color]}`}>
      <div className="text-3xl font-bold">{value}</div>
      <div className="text-xs mt-1 opacity-80">{label}</div>
    </div>
  );
}

export default function ReportTermini() {
  const { _locate } = useStateContext();
  const api = usePppApi();
  const { download, downloading } = useDownload();
  const [procedimenti, setProcedimenti] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingProc, setLoadingProc] = useState(true);

  useEffect(() => {
    api.listProcedimenti({
      onSuccess: (data) => { setProcedimenti(data.data ?? data); setLoadingProc(false); },
      onFormError: () => setLoadingProc(false),
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const carica = () => {
    if (!selectedId) return;
    setLoading(true);
    setReport(null);
    api.getReportTermini(selectedId, {
      onSuccess: (data) => { setReport(data); setLoading(false); },
      onFormError: () => setLoading(false),
    });
  };

  return (
    <div className="p-6">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          {_locate('ppp_report_termini_titolo')}
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {_locate('ppp_report_termini_desc')}
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-6 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs font-medium text-gray-500 mb-1">
            {_locate('ppp_report_seleziona_procedimento')}
          </label>
          {loadingProc ? <Spinner /> : (
            <select
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:text-white"
              value={selectedId}
              onChange={(e) => { setSelectedId(e.target.value); setReport(null); }}
            >
              <option value="">{_locate('ppp_report_scegli_procedimento')}</option>
              {procedimenti.map((p) => (
                <option key={p.id} value={p.id}>{p.codice ? `${p.codice} — ` : ''}{p.titolo}</option>
              ))}
            </select>
          )}
        </div>
        <button
          onClick={carica}
          disabled={!selectedId || loading}
          className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 disabled:opacity-50 transition-colors"
        >
          {loading ? _locate('ppp_report_caricamento') : _locate('ppp_report_genera')}
        </button>
        {report && (
          <>
            <button onClick={() => download(api.urlReportTerminiPdf(selectedId), `report_termini_ppp_${selectedId}.pdf`)} disabled={downloading} className="px-4 py-2 bg-gray-700 text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50 transition-colors">
              {downloading ? '...' : 'PDF'}
            </button>
            <button onClick={() => download(api.urlReportTerminiExcel(selectedId), `report_termini_ppp_${selectedId}.csv`)} disabled={downloading} className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors">
              {downloading ? '...' : 'Excel (CSV)'}
            </button>
          </>
        )}
      </div>

      {loading && <div className="flex justify-center items-center h-40"><Spinner /></div>}

      {report && (
        <div className="space-y-6">
          {/* KPI */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard label={_locate('ppp_report_termini_totale')} value={report.totaleScadenze} color="blue" />
            <KpiCard label={_locate('ppp_report_termini_rispettate')} value={report.rispettate} color="green" />
            <KpiCard label={_locate('ppp_report_termini_in_ritardo')} value={report.inRitardo} color="red" />
            <KpiCard label={_locate('ppp_report_termini_prorogate')} value={report.prorogate} color="orange" />
          </div>

          {/* Tabella scadenze */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
              <h2 className="font-semibold text-gray-900 dark:text-white">
                {report.codiceProcedimento} — {report.titoloProcedimento}
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_termini_col_scadenza')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_termini_col_normativa')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_termini_col_effettiva')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_termini_col_rispettata')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_stato')}</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_termine_legale')}</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_proroga')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_motivazione_proroga')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                  {report.scadenze.map((s) => (
                    <tr key={s.scadenzaId} className="hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900 dark:text-white">{s.descrizione}</div>
                        {s.riferimentoNormativo && <div className="text-xs text-gray-400 italic mt-0.5">{s.riferimentoNormativo}</div>}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        {new Date(s.dataScadenzaNormativa).toLocaleDateString('it-IT')}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        {s.dataScadenzaEffettiva ? new Date(s.dataScadenzaEffettiva).toLocaleDateString('it-IT') : '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        {s.dataRispettata ? new Date(s.dataRispettata).toLocaleDateString('it-IT') : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <StatoBadge stato={s.stato} />
                        {s.scarto !== null && s.scarto !== 0 && (
                          <div className={`text-xs mt-0.5 ${s.scarto > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {s.scarto > 0 ? '+' : ''}{s.scarto} gg
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-300">{s.termineLegalGiorni} gg</td>
                      <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-300">
                        {s.giorniProroga ? `+${s.giorniProroga} gg` : '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs max-w-[200px]">
                        {s.motivazioneProroga ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
